import type { FeedItem } from '@/components/ActivityFeed/ActivityFeed';
import {
  extractChanges,
  fieldNameList,
} from '@/components/ActivityFeed/normalize';
import type { LeadComment, LeadEvent } from '@/models/leads';

const LABELS: Record<string, string> = {
  firstName: 'First name',
  lastName: 'Last name',
  email: 'Email',
  phone: 'Phone',
  whatsapp: 'WhatsApp',
  telegram: 'Telegram',
  source: 'Source',
  sourceDetail: 'Source detail',
  website: 'Website',
  businessModel: 'Business model',
  originUrl: 'Origin URL',
  path: 'Path',
  utmMedium: 'UTM medium',
  utmCampaign: 'UTM campaign',
  referredByLeadId: 'Referred by (lead)',
  referringAgentId: 'Referred by',
  request: 'Original request',
  extendedMessage: 'Notes',
  emailStatus: 'Email status',
  lastContactAt: 'Last contact',
};

export function buildLeadFeed(
  events: LeadEvent[],
  comments: LeadComment[],
): FeedItem[] {
  const items: FeedItem[] = [];
  for (const e of events) {
    if (e.kind === 'commented') continue;
    const base = {
      id: `event-${e.id}`,
      at: e.createdAt,
      actor: e.actorName ?? e.actorEmail ?? null,
    };
    switch (e.kind) {
      case 'created':
        items.push({ ...base, kind: 'system', title: 'Created' });
        break;
      case 'field_updated': {
        const changes = extractChanges(e.payload, LABELS);
        items.push({
          ...base,
          kind: 'update',
          title: changes
            ? 'Updated'
            : `Updated ${fieldNameList(e.payload, LABELS)}`,
          changes,
        });
        break;
      }
      case 'status_changed':
        items.push({
          ...base,
          kind: 'status',
          title: `Status: ${String(e.payload?.from ?? '?')} → ${String(e.payload?.to ?? '?')}`,
        });
        break;
      case 'reason_changed':
        items.push({
          ...base,
          kind: 'status',
          title: `Reason: ${String(e.payload?.from ?? 'none')} → ${String(e.payload?.to ?? 'none')}`,
        });
        break;
      case 'assignee_changed':
        items.push({
          ...base,
          kind: 'system',
          title: 'Assignee changed',
          changes: [
            {
              label: 'Assignee',
              from: e.assigneeFromName ?? 'Unassigned',
              to: e.assigneeToName ?? 'Unassigned',
            },
          ],
        });
        break;
      case 'converted': {
        const contactId = e.payload?.contactId as number | undefined;
        items.push({
          ...base,
          kind: 'system',
          title: `Converted to contact${contactId ? ` #${contactId}` : ''}`,
        });
        break;
      }
      case 'qualified': {
        const contactId = e.payload?.contactId as number | undefined;
        items.push({
          ...base,
          kind: 'status',
          title: `Qualified${contactId ? ` - client #${contactId}` : ''}`,
        });
        break;
      }
      case 'stage_auto': {
        const cause =
          e.payload?.cause === 'invoice_paid' ? 'invoice paid' : 'offer sent';
        items.push({
          ...base,
          actor: 'System',
          kind: 'status',
          title: `Stage set by system: ${String(e.payload?.from ?? '?')} -> ${String(e.payload?.to ?? '?')} (${cause})`,
        });
        break;
      }
      default:
        items.push({ ...base, kind: 'system', title: e.kind });
    }
  }
  for (const c of comments) {
    items.push({
      id: `comment-${c.id}`,
      at: c.createdAt,
      actor: c.authorEmail ?? 'System',
      kind: 'comment',
      title: 'Comment',
      body: c.body,
    });
  }
  return items;
}
